import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const TestimonialComplete = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [cardsPerView, setCardsPerView] = useState(3);
  const autoScrollRef = useRef(null);

  const testimonials = [
    { id:1, name:"Shalini", place:"Kerala", initial:"S", color:"#E8D5C4", text:"I started this opportunity alongside my regular job on a friend's recommendation. With consistent effort and trust in the system, I was able to earn a good additional income and achieve the Star 500 rank in a short time. As a single parent, the flexibility of this opportunity made all the difference." },
    { id:2, name:"Jakkaraaju", place:"Telangana", initial:"J", color:"#C4D8E8", text:"I am building a meaningful business while helping many people grow and improve their lives. Compared to regular employment, this business offers flexibility, enjoyment, and long-term growth. It allows us to work with purpose while creating value for ourselves and others." },
    { id:3, name:"Sonia", place:"Kerala", initial:"S", color:"#D4E8C4", text:"I'm proud to have achieved the Star 500 rank in the company. This opportunity has empowered me and given me the confidence to support my family. I strongly encourage other women to take up this journey, as it truly has the potential to change lives." },
    { id:4, name:"Anjulatha Gupta", place:"Mumbai", initial:"A", color:"#E8C4D8", text:"Everyone who starts this journey can fulfill their dreams. When I began, I never imagined reaching an international level. I believed in the education system and the constant guidance and support provided, which helped me grow step by step. With belief, consistency, and hard work, you can achieve your goals." },
    { id:5, name:"Anusha", place:"Mangaluru", initial:"A", color:"#E8E4C4", text:"I joined this opportunity after experiencing the product iPulse. While managing my family and children, I achieved the Star 500 rank, which gave me confidence and personal growth. Like many others, I faced rejections in the beginning, but today I stand proud of this journey." },
    { id:6, name:"Anna Sweedal D'Souza", place:"Mangaluru", initial:"A", color:"#C4E8E4", text:"I began this journey as a housewife managing my family, never imagining I would reach this level of confidence and achievement. Through this opportunity, I achieved the Star 500 rank and experienced personal growth beyond my expectations. With the right guidance and belief, anything is possible." },
  ];

  useEffect(() => {
    const handleResize = () => {
      let n;
      if (window.innerWidth >= 1024) n = 3;
      else if (window.innerWidth >= 640) n = 2;
      else n = 1;
      setCardsPerView(prev => { if (prev !== n) setCurrentIndex(0); return n; });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = testimonials.length - cardsPerView;

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  useEffect(() => {
    if (!isHovered) autoScrollRef.current = setInterval(nextSlide, 2000);
    return () => clearInterval(autoScrollRef.current);
  }, [isHovered, nextSlide]);

  const cardWidth = 100 / cardsPerView;
  const dotCount = maxIndex + 1;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

        .testi-section {
          background: #fff;
          font-family: 'Outfit', sans-serif;
          padding: 90px 24px;
          position: relative;
          overflow: hidden;
        }
        .testi-section *, .testi-section *::before, .testi-section *::after {
          font-family: 'Outfit', sans-serif;
        }
        .testi-section h1, .testi-section h2, .testi-section h3,
        .testi-section h4, .testi-section h5, .testi-section h6 {
          font-family: 'Cormorant Garamond', serif;
        }

        .testi-section::before {
          content: ''; position: absolute; top: -200px; right: -200px;
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(255,200,100,0.06) 0%, transparent 70%);
          pointer-events: none;
        }
        .testi-section::after {
          content: ''; position: absolute; bottom: -150px; left: -150px;
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(100,200,255,0.05) 0%, transparent 70%);
          pointer-events: none;
        }

        .testi-inner { max-width: 1300px; margin: 0 auto; }

        .testi-header {
          display: flex; align-items: flex-end; justify-content: space-between;
          margin-bottom: 56px; flex-wrap: wrap; gap: 24px;
        }
        .testi-title-group { display: flex; flex-direction: column; gap: 8px; }

        .testi-eyebrow {
          font-family: 'Outfit', sans-serif;
          font-size: 11px; font-weight: 500; letter-spacing: 0.2em;
          text-transform: uppercase; color: #000;
        }
        .testi-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(32px, 5vw, 54px); font-weight: 600;
          color: #000; line-height: 1.1; margin: 0;
        }

        .testi-nav { display: flex; gap: 10px; flex-shrink: 0; }
        .nav-btn {
          width: 46px; height: 46px; border-radius: 50%;
          border: 1px solid rgba(0,0,0,0.12); background: transparent;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.25s ease;
        }
        .nav-btn:hover:not(:disabled) {
          background: #f0c060; border-color: #f0c060; transform: scale(1.05);
        }
        .nav-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        .testi-track-wrapper { overflow: hidden; border-radius: 4px; }
        .testi-track { display: flex; transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94); }

        .testi-card { flex-shrink: 0; padding: 0 10px; box-sizing: border-box; }
        .card-inner {
          background: rgb(15 23 42 / var(--tw-bg-opacity, 1));
          border: 1px solid rgba(59,123,255,0.25);
          border-radius: 16px; padding: 36px 32px 32px;
          height: 100%; display: flex; flex-direction: column;
          position: relative; transition: border-color 0.3s ease, transform 0.3s ease;
          cursor: default; box-sizing: border-box; min-height: 280px;
        }
        .card-inner:hover { border-color: rgba(240,192,96,0.3); transform: translateY(-3px); }

        .quote-icon { position: absolute; top: 28px; right: 28px; opacity: 0.12; }

        .card-text {
          font-family: 'Outfit', sans-serif;
          font-size: 14.5px; line-height: 1.75;
          color: rgba(255,255,255,0.65); flex-grow: 1; margin-bottom: 28px; font-weight: 300;
        }
        .card-author {
          display: flex; align-items: center; gap: 14px;
          padding-top: 20px; border-top: 1px solid rgba(255,255,255,0.07);
        }
        .author-avatar {
          width: 42px; height: 42px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-family: 'Cormorant Garamond', serif;
          font-size: 17px; font-weight: 600; color: #1a1a1a; flex-shrink: 0;
        }
        .author-name {
          font-family: 'Outfit', sans-serif;
          font-size: 15px; font-weight: 500; color: #f5f0e8;
          display: block; line-height: 1.2;
        }
        .author-place {
          font-family: 'Outfit', sans-serif;
          font-size: 12px; color: rgb(249, 243, 243);
          display: block; margin-top: 2px; font-weight: 300;
        }

        .testi-dots { display: flex; justify-content: center; gap: 6px; margin-top: 40px; }
        .dot {
          height: 3px; border-radius: 2px; background: rgba(255,255,255,0.15);
          border: none; cursor: pointer; transition: all 0.3s ease; width: 24px; padding: 0;
        }
        .dot.active { background: #f0c060; width: 48px; }

        .progress-bar {
          position: absolute; bottom: 0; left: 0; height: 2px;
          background: #f0c060; border-radius: 0 2px 2px 0;
          animation: progress 3.5s linear infinite;
        }
        @keyframes progress { from { width: 0%; } to { width: 100%; } }
        .progress-bar.paused { animation-play-state: paused; }
      `}} />

      <section
        className="testi-section"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={`progress-bar ${isHovered ? 'paused' : ''}`} key={currentIndex + isHovered} />

        <div className="testi-inner">
          <div className="testi-header">
            <div className="testi-title-group">
              <span className="testi-eyebrow">What they say</span>
              <h2 className="testi-title">Voices of Our Community</h2>
            </div>
            <div className="testi-nav">
              <button className="nav-btn" onClick={prevSlide} aria-label="Previous">
                <ChevronLeft size={18} color="#000" />
              </button>
              <button className="nav-btn" onClick={nextSlide} aria-label="Next">
                <ChevronRight size={18} color="#000" />
              </button>
            </div>
          </div>

          <div className="testi-track-wrapper">
            <div className="testi-track" style={{ transform: `translateX(-${currentIndex * cardWidth}%)` }}>
              {testimonials.map((t) => (
                <div key={t.id} className="testi-card" style={{ width: `${cardWidth}%` }}>
                  <div className="card-inner">
                    <Quote className="quote-icon" size={52} color="#f0c060" />
                    <p className="card-text">"{t.text}"</p>
                    <div className="card-author">
                      <div className="author-avatar" style={{ background: t.color }}>{t.initial}</div>
                      <div>
                        <span className="author-name">{t.name}</span>
                        <span className="author-place">{t.place}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="testi-dots">
            {[...Array(dotCount)].map((_, i) => (
              <button key={i} className={`dot ${currentIndex === i ? 'active' : ''}`} onClick={() => setCurrentIndex(i)} aria-label={`Go to slide ${i + 1}`} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default TestimonialComplete;